import React from "react";
import { motion } from "framer-motion";
import { Compass, Radio, Coins, Layers } from "lucide-react";

const LAYERS = [
  {
    id: "discover",
    name: "Discover",
    tag: "Layer 01 · Intelligence",
    icon: Compass,
    desc: "AI-powered content intelligence that organizes signal across the web — surfacing what matters, ranking by integrity, not virality.",
    bullets: ["Semantic indexing", "Source weighting", "Real-time signal graph"],
  },
  {
    id: "engage",
    name: "Engage",
    tag: "Layer 02 · Experience",
    icon: Radio,
    desc: "A directed-intent engagement ecosystem. Viewers come with purpose; the platform rewards depth, recall, and informed action.",
    bullets: ["Directed-intent UX", "Knowledge rewards", "Owned audience graph"],
  },
  {
    id: "monetize",
    name: "Monetize",
    tag: "Layer 03 · Value",
    icon: Coins,
    desc: "A creator and advertiser value engine. Premium adjacency, transparent attribution, durable revenue across the stack.",
    bullets: ["Creator revenue stack", "Premium ad surfaces", "Lift-based attribution"],
  },
];

export default function Platform() {
  return (
    <section
      id="platform"
      data-testid="section-platform"
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg-fine opacity-30 pointer-events-none" />
      <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-violet-600/5 blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-7">
            <div className="text-[11px] font-mono tracking-[0.3em] text-violet-300 uppercase mb-6">
              Chapter 02 · Platform
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-[1.0]">
              A new media
              <br />
              <span className="text-zinc-400">operating system</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-zinc-400 text-base leading-relaxed">
              Three layers, one stack. Each module is independently powerful and
              compounds in concert — the operating system for premium media.
            </p>
          </div>
        </div>

        {/* Architecture diagram */}
        <div className="relative border border-white/10 bg-black/40 backdrop-blur-sm">
          {/* top header */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-white/10 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">
            <div className="flex items-center gap-2">
              <Layers className="w-3.5 h-3.5" strokeWidth={1.5} />
              F3 / Architecture
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
              Live · v1.0
            </div>
          </div>

          {/* Layers */}
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
            {LAYERS.map((l, i) => {
              const Icon = l.icon;
              return (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  data-testid={`platform-layer-${l.id}`}
                  className="group p-8 md:p-10 relative hover:bg-zinc-950/50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-8">
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">
                      {l.tag}
                    </div>
                    <div className="w-10 h-10 border border-white/15 flex items-center justify-center group-hover:border-violet-500/60 transition-colors">
                      <Icon className="w-4 h-4 text-zinc-300 group-hover:text-violet-300" strokeWidth={1.5} />
                    </div>
                  </div>

                  <h3 className="font-serif-display text-2xl md:text-3xl text-white tracking-tight mb-3">
                    {l.name}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed mb-8">{l.desc}</p>

                  <div className="space-y-2 pt-6 border-t border-white/10">
                    {l.bullets.map((b) => (
                      <div key={b} className="flex items-center gap-3 text-[12px] font-mono text-zinc-500">
                        <span className="text-violet-300">+</span>
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
